import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import type { ThemePreference } from '../types/theme'
import { getThemePreference, setThemePreference } from '../utils/userPreferences'

interface ThemeContextValue {
  theme: ThemePreference
  setTheme: (theme: ThemePreference) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

interface ThemeProviderProps {
  children: ReactNode
}

function applyThemeToDocument(theme: ThemePreference) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.dataset.theme = theme
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    // Try stored preference first
    const stored = getThemePreference()
    return stored
  })

  useEffect(() => {
    applyThemeToDocument(theme)
  }, [theme])

  const setTheme = (next: ThemePreference) => {
    setThemePreference(next)
    setThemeState(next)
  }

  const value: ThemeContextValue = {
    theme,
    setTheme,
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext)
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return ctx
}


